import { PublicIdSchemas } from '@idc-forms/id'
import { Period } from '@idc-forms/types/period'
import { z } from 'zod'
import {
  ATTACHMENT_VISIBILITY,
  NOTIFICATION_RECIPIENT,
  PORTAL_VALIDATION_TYPE,
  REQUIREMENT_APPLIES_TO,
  STATE_TYPE
} from '../entities/state/state'
import { optionalString, requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de estados del flujo. Es el contrato OpenAPI
 * que consume el SDK generado, el panel admin y —vía `lookup`— la matriz de
 * permisos por estado del editor de roles v2.
 *
 * `name` es único GLOBAL: la UI identifica al estado por nombre y la URL legacy
 * navegaba por él.
 *
 * DOS CAMPOS DE SISTEMA NO ENTRAN AL INPUT, deliberadamente:
 *
 * - `type` — `init`/`end`/`reject`/`rescind`/`inactive`/`undefined` son filas
 *   sembradas que el motor de flujos interpreta por su tipo. Por el API sólo
 *   nacen estados `process` y una edición nunca cambia el tipo: aceptarlo en el
 *   wire permitiría convertir un estado corriente en el terminal del flujo.
 * - `blocked` — marca de sistema que protege al estado del borrado. Si viajara
 *   en el input, cualquiera se desbloquearía a sí mismo antes de borrar y la
 *   guarda no valdría nada.
 *
 * `attachmentRequirements` (los requisitos documentales del portal) NO se
 * escribe por el recurso del estado: es un SUBRECURSO propio
 * (`/admin/states/:id/attachment-requirements`), y la fila del estado expone
 * sólo su conteo. Las mutaciones del estado escriben campos explícitos, así que
 * un PATCH no los toca ni los pisa.
 */

/**
 * Autoría wire-safe: el `_id` del usuario no cruza el adapter, así que se publica su
 * `usr_`. Va entera nullable porque el legacy guarda `createdBy: null` en las filas
 * sembradas, y la UI hace mucho que resuelve ese caso a «—».
 */
export const V2AdminStateAuthorSchema = z
  .object({
    id: PublicIdSchemas.user,
    fullname: z.string()
  })
  .nullable()

/** Fila del listado y del detalle: un estado es una entidad hoja, sin relaciones. */
export const V2AdminStateSchema = z.object({
  id: PublicIdSchemas.state,
  name: z.string().min(1),
  description: z.string(),
  /** Color del chip en la UI, formato `#rrggbb`. */
  color: z.string(),
  /** SLA: cantidad de `interval` antes de considerarse vencido el estado. */
  time: z.number().int().nonnegative(),
  interval: z.nativeEnum(Period),
  notification: z.array(z.nativeEnum(NOTIFICATION_RECIPIENT)),
  emailsNotification: z.array(z.string()),
  type: z.nativeEnum(STATE_TYPE),
  active: z.boolean(),
  /** De sistema: un estado bloqueado no se puede borrar. Sólo lectura. */
  blocked: z.boolean(),
  visible: z.boolean(),
  sendSAP: z.boolean(),
  /** Requisitos documentales del portal configurados en el estado (sólo conteo). */
  attachmentRequirementsCount: z.number().int().nonnegative(),
  createdBy: V2AdminStateAuthorSchema,
  updatedBy: V2AdminStateAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminStatesPageSchema = z.object({
  data: z.array(V2AdminStateSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y facetas con los mismos filtros allowlist del listado. Cada faceta
 * ignora el filtro de SU PROPIA dimensión (si se mirara a sí misma, al elegir un
 * valor el resto saldría en cero y el panel afirmaría que no existe); `total` sí
 * respeta la consulta completa.
 *
 * Las dos dimensiones son de baja cardinalidad y sirven para navegar: `type`
 * tiene siete valores y `active` dos.
 */
export const V2AdminStatesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  }),
  type: z.record(z.nativeEnum(STATE_TYPE), z.number().int().nonnegative())
})

/**
 * Entrada mínima del selector de estados: lo que necesitan la matriz de permisos
 * del editor de roles y, más adelante, el builder de workflows. Identifica por
 * `sta_` y nunca expone `_id` ni el uuid legacy.
 */
export const V2AdminStateLookupItemSchema = z.object({
  id: PublicIdSchemas.state,
  name: z.string().min(1),
  color: z.string(),
  type: z.nativeEnum(STATE_TYPE)
})

export const V2AdminStateLookupSchema = z.object({
  data: z.array(V2AdminStateLookupItemSchema)
})

/**
 * Alta de estado. `name` se recorta y debe ser único (case-insensitive) entre
 * estados vivos. El estado nace `process`: `type` y `blocked` son de sistema y
 * no viajan en el wire.
 */
export const V2CreateAdminStateSchema = z.object({
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'El color debe ser hexadecimal (#rrggbb)')
    .optional(),
  time: z.number().int().nonnegative().optional(),
  interval: z.nativeEnum(Period).optional(),
  notification: z.array(z.nativeEnum(NOTIFICATION_RECIPIENT)).default([]),
  /**
   * Sólo se usan cuando `notification` incluye `custom`; se validan como correos
   * para que una dirección rota no se descubra recién al despachar el aviso.
   */
  emailsNotification: z.array(z.string().email()).default([]),
  active: z.boolean().optional(),
  visible: z.boolean().optional(),
  sendSAP: z.boolean().optional()
})

/** Edición parcial. Un id desconocido responde 404: nunca hace upsert. */
export const V2UpdateAdminStateSchema = V2CreateAdminStateSchema.partial()

/**
 * Requisito documental del portal, ya persistido. Se identifica por `atr_`; el
 * uuid interno del subdocumento —join key de los adjuntos ya subidos— no cruza
 * el wire.
 */
export const V2AdminStateAttachmentRequirementSchema = z.object({
  id: PublicIdSchemas.attachmentRequirement,
  name: z.string().min(1),
  description: z.string(),
  /** Extensiones admitidas, tal como las configuró el editor (`pdf`, `jpg`, …). */
  fileTypesAllowed: z.array(z.string()),
  /** Tamaño máximo por archivo, en MB. */
  maxFileSize: z.number().int().positive(),
  /** Obligatorio para el FLUJO INTERNO (el asesor no puede avanzar sin él). */
  isRequired: z.boolean(),
  /** Obligatorio para el CLIENTE en el formulario público. Es independiente de `isRequired`. */
  clientRequired: z.boolean(),
  appliesTo: z.nativeEnum(REQUIREMENT_APPLIES_TO),
  /** Validaciones del portal a las que aplica. Vacío = todas. */
  validationTypes: z.array(z.nativeEnum(PORTAL_VALIDATION_TYPE)),
  /** `internal` NUNCA lo ve el cliente: sólo el asesor lo sube y lo lee. */
  visibility: z.nativeEnum(ATTACHMENT_VISIBILITY)
})

export const V2AdminStateAttachmentRequirementsSchema = z.object({
  data: z.array(V2AdminStateAttachmentRequirementSchema)
})

/**
 * Un requisito tal como lo manda el editor. `id` presente = conserva la fila (y
 * con ella los adjuntos que ya la referencian); ausente = nace.
 *
 * Un `atr_` que no pertenezca a ESTE estado es un 404: sin esa guarda, mandar el
 * id de un requisito de otro estado lo mudaría de dueño en silencio.
 */
export const V2AdminStateAttachmentRequirementInputSchema = z.object({
  id: PublicIdSchemas.attachmentRequirement.optional(),
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  description: optionalString(),
  fileTypesAllowed: z.array(requiredString()).min(1, 'Elige al menos un tipo de archivo'),
  maxFileSize: z.number().int().positive('El tamaño máximo debe ser al menos 1 MB'),
  isRequired: z.boolean().optional(),
  clientRequired: z.boolean().optional(),
  appliesTo: z.nativeEnum(REQUIREMENT_APPLIES_TO).optional(),
  validationTypes: z.array(z.nativeEnum(PORTAL_VALIDATION_TYPE)).optional(),
  visibility: z.nativeEnum(ATTACHMENT_VISIBILITY).optional()
})

/**
 * REEMPLAZA la lista entera (por eso es `PUT` y no `PATCH`): lo que no viaja se
 * borra. El editor administra el array completo en memoria y lo guarda de una,
 * así que un CRUD por ítem lo obligaría a diffear contra lo cargado — y un diff
 * mal calculado borra requisitos con adjuntos colgando.
 *
 * Es el mismo criterio que `states` en el PATCH de workflows: una lista se
 * reemplaza, no se mergea.
 */
export const V2ReplaceAdminStateAttachmentRequirementsSchema = z.object({
  data: z.array(V2AdminStateAttachmentRequirementInputSchema)
})

export type V2AdminStateAuthor = z.infer<typeof V2AdminStateAuthorSchema>
export type V2AdminState = z.infer<typeof V2AdminStateSchema>
export type V2AdminStatesPage = z.infer<typeof V2AdminStatesPageSchema>
export type V2AdminStatesSummary = z.infer<typeof V2AdminStatesSummarySchema>
export type V2AdminStateLookupItem = z.infer<typeof V2AdminStateLookupItemSchema>
export type V2AdminStateLookup = z.infer<typeof V2AdminStateLookupSchema>
export type V2CreateAdminStateInput = z.infer<typeof V2CreateAdminStateSchema>
export type V2UpdateAdminStateInput = z.infer<typeof V2UpdateAdminStateSchema>
export type V2AdminStateAttachmentRequirement = z.infer<
  typeof V2AdminStateAttachmentRequirementSchema
>
export type V2AdminStateAttachmentRequirements = z.infer<
  typeof V2AdminStateAttachmentRequirementsSchema
>
export type V2AdminStateAttachmentRequirementInput = z.infer<
  typeof V2AdminStateAttachmentRequirementInputSchema
>
export type V2ReplaceAdminStateAttachmentRequirementsInput = z.infer<
  typeof V2ReplaceAdminStateAttachmentRequirementsSchema
>
